import React, { useContext, useEffect, useMemo } from "react"
import { StyleProp, View, ViewStyle } from "react-native"
import Animated, {
  Easing,
  cancelAnimation,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from "react-native-reanimated"
import { Svg, Circle } from "react-native-svg"
import { ThemeContext } from "../utility/context/ThemeContext"

type Props = {
  size?: 'small' | 'medium' | 'large' | number
  colors?: {
    color1?: string
    color2?: string
    color3?: string
  }
  style?: StyleProp<ViewStyle>
}

const MIN_DIAMETER = 10

const SIZE_MAP: Record<'small' | 'medium' | 'large', number> = {
  small: 30,
  medium: 40,
  large: 50,
}

// Each arc rotates at a different period so the three arcs visually drift apart
// and re-collide, giving the spinner movement that isn't a monotonic spin.
const ARC_DURATIONS_MS = [1000, 1300, 1700] as const

type ArcSpec = {
  color: string
  durationMs: number
  reverse: boolean
  radiusRatio: number
  arcFraction: number
}

const ArcRing = ({
  spec,
  diameter,
  strokeWidth,
}: {
  spec: ArcSpec
  diameter: number
  strokeWidth: number
}): JSX.Element => {
  const rotation = useSharedValue(0)

  useEffect(() => {
    rotation.value = withRepeat(
      withTiming(spec.reverse ? -360 : 360, {
        duration: spec.durationMs,
        easing: Easing.linear,
      }),
      -1,
      false
    )
    return () => {
      cancelAnimation(rotation)
    }
  }, [rotation, spec.durationMs, spec.reverse])

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ rotate: `${rotation.value}deg` }],
  }))

  const radius = ((diameter * spec.radiusRatio) - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const dashLength = circumference * spec.arcFraction
  const gapLength = Math.max(0, circumference - dashLength)
  const center = diameter / 2

  return (
    <Animated.View
      style={[
        {
          position: "absolute",
          width: diameter,
          height: diameter,
          alignItems: "center",
          justifyContent: "center",
        },
        animatedStyle,
      ]}
    >
      <Svg width={diameter} height={diameter}>
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={spec.color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          fill="none"
          strokeDasharray={`${dashLength} ${gapLength}`}
        />
      </Svg>
    </Animated.View>
  )
}

const Loader = ({ size = 'small', style, colors }: Props): React.ReactElement => {
  const theme = useContext(ThemeContext).theme
  const { loadingAnimation } = theme.colors

  // default to small size
  let diameter = SIZE_MAP.small
  // if size is a string used in SIZE_MAP, use the value
  if (typeof size === 'string' && size in SIZE_MAP) {
    diameter = SIZE_MAP[size as keyof typeof SIZE_MAP]
  }
  // if size is a number, use the value
  if (typeof size === 'number' && Number.isFinite(size) && size > 0) {
    diameter = Math.max(MIN_DIAMETER, size)
  }

  // Thinner strokes leave a small visible gap between concentric rings while
  // still scaling cleanly down to the small size.
  const strokeWidth = Math.max(2, Math.round(diameter * 0.07))

  const specs: ArcSpec[] = useMemo(() => [
    {
      color: colors?.color1 || loadingAnimation.color1,
      durationMs: ARC_DURATIONS_MS[0],
      reverse: false,
      radiusRatio: 1,
      arcFraction: 0.28,
    },
    {
      color: colors?.color2 || loadingAnimation.color2,
      durationMs: ARC_DURATIONS_MS[1],
      reverse: true,
      radiusRatio: 0.72,
      arcFraction: 0.32,
    },
    {
      color: colors?.color3 || loadingAnimation.color3,
      durationMs: ARC_DURATIONS_MS[2],
      reverse: false,
      radiusRatio: 0.44,
      arcFraction: 0.36,
    },
  ], [
    colors?.color1,
    colors?.color2,
    colors?.color3,
    loadingAnimation.color1,
    loadingAnimation.color2,
    loadingAnimation.color3,
  ])

  return (
    <View
      style={[
        {
          width: diameter,
          height: diameter,
          alignItems: "center",
          justifyContent: "center",
        },
        style,
      ]}
    >
      {specs.map((spec, idx) => (
        <ArcRing
          key={`arc-${idx}`}
          spec={spec}
          diameter={diameter}
          strokeWidth={strokeWidth}
        />
      ))}
    </View>
  )
}

export default Loader
